import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkRehype from 'remark-rehype';
import rehypeSanitize from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import { ArrowLeft, Pencil, ExternalLink } from 'lucide-react';
import { Button } from '@glob/ui';
import { useVaultStore } from '../../features/auth/vaultStore';
import {
  openItem,
  type MemoPayload,
  type LinkPayload,
  type SnippetPayload,
} from '../../features/items/data/items';
import type { CachedItem } from '../../lib/cache/idb';
import { MemoEditor } from '../../features/items/editors/MemoEditor';
import { LinkEditor } from '../../features/items/editors/LinkEditor';
import { SnippetEditor } from '../../features/items/editors/SnippetEditor';
import { useT } from '../../i18n';

// GitHub allow-list sanitization; markdown is always client-rendered (ZK §3).
const markdownProcessor = unified()
  .use(remarkParse)
  .use(remarkGfm)
  .use(remarkRehype)
  .use(rehypeSanitize)
  .use(rehypeStringify);

/** Render sanitized markdown to HTML, recomputing when the source changes. */
function useMarkdown(source: string | undefined): string {
  const [html, setHtml] = useState('');
  useEffect(() => {
    let cancelled = false;
    if (source) {
      void markdownProcessor.process(source).then((file) => {
        if (!cancelled) setHtml(String(file));
      });
    } else {
      setHtml('');
    }
    return () => {
      cancelled = true;
    };
  }, [source]);
  return html;
}

function MemoView({ payload }: { payload: MemoPayload }) {
  const t = useT();
  const html = useMarkdown(payload.body_markdown);
  return (
    <article className="space-y-4">
      <h1 className="text-2xl font-display tracking-tight">
        {payload.title || t('items.untitled')}
      </h1>
      <div
        className="prose prose-sm max-w-none text-text-2"
        // sanitized by rehype-sanitize (GitHub allow-list); CSP forbids inline
        // scripts as defence in depth.
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </article>
  );
}

function LinkView({ payload }: { payload: LinkPayload }) {
  const t = useT();
  const notesHtml = useMarkdown(payload.notes);
  return (
    <article className="space-y-4">
      <h1 className="text-2xl font-display tracking-tight">
        {payload.title || t('items.untitled')}
      </h1>
      {payload.url && (
        <a
          href={payload.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline break-all"
        >
          <ExternalLink size={14} aria-hidden className="shrink-0" />
          {payload.url}
        </a>
      )}
      {payload.description && (
        <p className="text-sm text-text-3 leading-relaxed">{payload.description}</p>
      )}
      {payload.notes && (
        <div
          className="prose prose-sm max-w-none text-text-2"
          // notes is markdown, always client-rendered + sanitized (ZK §3).
          dangerouslySetInnerHTML={{ __html: notesHtml }}
        />
      )}
    </article>
  );
}

function SnippetView({ payload }: { payload: SnippetPayload }) {
  const t = useT();
  return (
    <article className="space-y-4">
      <h1 className="text-2xl font-display tracking-tight">
        {payload.title || t('items.untitled')}
      </h1>
      {payload.description && (
        <p className="text-sm text-text-3 leading-relaxed">{payload.description}</p>
      )}
      <div className="space-y-4">
        {payload.files.map((file, i) => (
          <figure key={i} className="rounded-lg border border-border overflow-hidden">
            <figcaption className="flex items-center justify-between px-3 py-1.5 bg-bg-2 border-b border-border text-xs font-mono text-text-3">
              <span className="truncate">{file.name || t('items.untitled')}</span>
              <span className="text-text-4">{file.language}</span>
            </figcaption>
            {/* Shiki is available but kept out of the read path to avoid blocking
                on grammar loads; a plain pre/code is the light default. */}
            <pre className="overflow-x-auto p-3 bg-bg-1 text-sm">
              <code className="font-mono text-text-1">{file.content}</code>
            </pre>
          </figure>
        ))}
      </div>
    </article>
  );
}

/** The in-place editor for the item's type. Edits flow cache → dirty → flush. */
function ItemEditor({ item, payload }: { item: CachedItem; payload: unknown }) {
  switch (item.item_type) {
    case 'memo': {
      const p = payload as MemoPayload;
      return (
        <MemoEditor
          itemUuid={item.uuid}
          initial={{ title: p.title, body_markdown: p.body_markdown }}
        />
      );
    }
    case 'link': {
      const p = payload as LinkPayload;
      return (
        <LinkEditor
          itemUuid={item.uuid}
          initial={{
            url: p.url,
            title: p.title,
            description: p.description,
            notes: p.notes,
          }}
        />
      );
    }
    case 'snippet': {
      const p = payload as SnippetPayload;
      return (
        <SnippetEditor
          itemUuid={item.uuid}
          initial={{
            title: p.title,
            description: p.description,
            files: p.files,
            tag_local_ids: p.tag_local_ids,
          }}
        />
      );
    }
  }
}

/**
 * Read/edit view for an item. Fetches (cache or server), decrypts under the VK,
 * and renders per type. Toggling edit mode mounts the matching in-place editor
 * (useItemEditor), so edits persist via cache → dirty → sync flush.
 */
function ItemView() {
  const t = useT();
  const navigate = useNavigate();
  const { itemId } = Route.useParams();
  const vault = useVaultStore((s) => s.vault);
  const userUuid = useVaultStore((s) => s.userUuid);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  // Leaving edit mode re-reads the (now updated) cache so the view reflects the
  // edit, and refreshes the list's title. The editor already wrote cache+dirty.
  const toggleEdit = () => {
    setEditing((e) => {
      if (e) {
        void queryClient.invalidateQueries({ queryKey: ['item', itemId] });
        void queryClient.invalidateQueries({ queryKey: ['items'] });
      }
      return !e;
    });
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['item', itemId],
    enabled: Boolean(vault && userUuid),
    queryFn: async () => {
      if (!vault || !userUuid) throw new Error('locked');
      return openItem({ vault, userUuid }, itemId);
    },
  });

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => void navigate({ to: '/app' })}
          className="flex items-center gap-1.5 text-xs text-text-3 hover:text-text-1 transition-colors"
        >
          <ArrowLeft size={13} aria-hidden /> {t('items.back')}
        </button>
        {data && (
          <Button size="sm" variant="ghost" onClick={toggleEdit}>
            {editing ? (
              t('items.done')
            ) : (
              <>
                <Pencil size={13} aria-hidden /> {t('items.edit')}
              </>
            )}
          </Button>
        )}
      </div>

      {isLoading && <p className="text-sm text-text-3">{t('items.loading')}</p>}
      {isError && (
        <p role="alert" className="text-sm text-error">
          {t('items.load-error')}
        </p>
      )}

      {data && editing && <ItemEditor item={data.item} payload={data.payload} />}
      {data && !editing && data.item.item_type === 'memo' && (
        <MemoView payload={data.payload as MemoPayload} />
      )}
      {data && !editing && data.item.item_type === 'link' && (
        <LinkView payload={data.payload as LinkPayload} />
      )}
      {data && !editing && data.item.item_type === 'snippet' && (
        <SnippetView payload={data.payload as SnippetPayload} />
      )}
    </div>
  );
}

export const Route = createFileRoute('/_authed/app/item/$itemId')({
  component: ItemView,
});
